import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  ElementRef,
  InjectionToken,
  OnChanges,
  afterNextRender,
  effect,
  inject,
  input,
  signal,
  viewChild,
} from '@angular/core';
import { ActiveElement, Chart, ChartConfiguration, ChartData, ChartEvent } from 'chart.js';
import { ChartLegendItem, ChartLegendComponent } from '../chart-legend/chart-legend.component';
import { CurrencyPipe } from '@angular/common';
import { UiIconComponent } from '../../ui/icon/icon.component';
import { ThemeService } from '../../services/theme.service';

type DoughnutChart = Chart<'doughnut', number[], unknown>;
type DoughnutChartConfig = ChartConfiguration<'doughnut', number[], unknown>;

/** Creates the Chart.js instance; specs swap it to observe construction without a canvas context. */
export const DOUGHNUT_CHART_FACTORY = new InjectionToken<(canvas: HTMLCanvasElement, config: DoughnutChartConfig) => DoughnutChart>(
  'DOUGHNUT_CHART_FACTORY',
  { providedIn: 'root', factory: () => (canvas, config) => new Chart(canvas, config) },
);

export interface ChartDataInput {
  label: string;
  amount: number;
}

interface DoughnutDataSet extends ChartDataInput {
  color: string;
  percentage: number;
}

@Component({
  selector: 'doughnut-chart',
  templateUrl: './doughnut-chart.component.html',
  styleUrl: './doughnut-chart.component.scss',
  imports: [ChartLegendComponent, UiIconComponent, CurrencyPipe],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DoughnutChartComponent implements OnChanges {
  data = input<ChartDataInput[] | null>();
  readonly chartContextRef = viewChild.required<ElementRef<HTMLCanvasElement>>('ctx');
  showToolTips = input(true);
  showLegend = input(true);
  showTitle = input(true);
  height = input(250);
  width = input(250);
  preview = input(false);
  title = input('');
  emptyTitle = input('');
  private chart?: Chart<'doughnut', number[], unknown>;
  // Chart.js sizes the canvas when the chart is created, so wait until it is in the DOM.
  // Projected content (such as a drawer body) can be created before it is attached.
  private rendered = false;
  public readonly hoveredIndex = signal<number | null>(null);
  public readonly legendItems = signal<ChartLegendItem[]>([]);
  private readonly theme = inject(ThemeService);
  private readonly createChart = inject(DOUGHNUT_CHART_FACTORY);
  private get chartColors(): string[] {
    return this.theme.chartPalette();
  }
  private get borderColor(): string {
    return this.theme.resolveCssToken('--app-surface-elevated');
  }

  constructor() {
    effect(() => {
      // Touch the palette signal so this effect re-runs on theme change.
      this.theme.chartPalette();
      if (this.chart?.data) this.updateChart();
    });
    afterNextRender(() => {
      this.rendered = true;
      this.updateChart();
    });
    inject(DestroyRef).onDestroy(() => this.chart?.destroy());
  }

  get total(): number {
    return this.data()?.reduce((acc, curr) => acc + curr.amount, 0) ?? 0;
  }

  ngOnChanges() {
    if (this.rendered) this.updateChart();
  }

  private configureData(): DoughnutDataSet[] {
    const chartData = this.data() ?? [];
    const total = chartData.map(d => d.amount).reduce((a, b) => a + b, 0);
    const palette = this.chartColors;
    const fallback = palette[0] ?? '#14b8a6';
    return chartData.map((d, i) => ({
      ...d,
      color: palette[i % palette.length] ?? fallback,
      percentage: total > 0 ? d.amount / total : 0,
    }));
  }

  private buildDatasets(data: DoughnutDataSet[]): ChartData<'doughnut', number[], unknown> {
    return {
      datasets: [
        {
          data: data.map(d => d.amount),
          hoverBorderWidth: this.preview() ? 1 : 2,
          borderWidth: this.preview() ? 1 : 2,
          hoverOffset: this.preview() ? 0 : 4,
          borderColor: this.borderColor,
          hoverBorderColor: this.borderColor,
          backgroundColor: data.map(d => d.color),
        },
      ],
      labels: data.map(d => d.label),
    };
  }

  private buildLegendItems(data: DoughnutDataSet[]) {
    return data.map(d => ({
      label: `${d.label}: ${new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: 'USD',
        maximumSignificantDigits: 4,
      }).format(d.amount)}`,
      color: d.color,
      hovered: false,
    }));
  }

  updateChart() {
    if (!this.chart?.data) return this.initChart();

    const _data = this.configureData();
    this.legendItems.set(this.buildLegendItems(_data));
    this.chart.data = this.buildDatasets(_data);
    this.chart.update();
  }

  public onHover(event: ChartEvent, elements: ActiveElement[], _chart: Chart<'doughnut', number[], unknown>) {
    const index = elements[0]?.index ?? null;

    this.hoveredIndex.set(index);
  }

  public initChart() {
    if (!this.data()) return;
    const _data = this.configureData();
    this.legendItems.set(this.buildLegendItems(_data));
    const ctx = this.chartContextRef().nativeElement;

    this.chart = this.createChart(ctx, {
      type: 'doughnut',
      data: this.buildDatasets(_data),
      options: {
        layout: {
          padding: this.preview() ? 0 : 6,
        },
        animation: this.preview() ? false : undefined,
        responsive: true,
        maintainAspectRatio: false,
        cutout: '70%',
        onHover: this.onHover.bind(this),
        plugins: {
          legend: {
            display: false,
          },
          tooltip: {
            enabled: !this.preview() && this.showToolTips(),
            padding: 8,
            callbacks: {
              label: function (context) {
                var label = context.label || '';

                if (context.raw) {
                  return `${label}: ${new Intl.NumberFormat('en-US', {
                    style: 'currency',
                    currency: 'USD',
                    maximumSignificantDigits: 3,
                  }).format(context.raw as number)} (${Math.round(
                    ((context.raw as number) / _data.reduce((acc, curr) => acc + curr.amount, 0)) * 100,
                  )}%)`;
                }
                return label;
              },
            },
          },
        },
      },
    } as ChartConfiguration<'doughnut'>);
  }
}
